/**
 * Parser desacoplado para la seccion DESDE 07/94 del PDF ANSES.
 * Esta logica NO reemplaza al Resumen; solo genera ajustes por REM IMP. SS en [0, 1].
 */

const TITULO_INICIO_REGEX = /DESDE\s+07\/94\b/i;
const TITULO_FIN_REGEX = /HIST(?:[ÓO]RICO|ORICO)\s+DE\s+AUT[ÓO]NOM(?:O|OS)/i;
const PERIODO_REGEX = /^(0?[1-9]|1[0-2])\/\d{4}$/;
const CUIT_REGEX = /^(\d{2}-\d{8}-\d|\d{11}|\d{2}\s+\d{8}\s+\d)$/;

const removeDiacritics = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

/**
 * @typedef {Object} Desde0794Row
 * @property {string} razonSocial
 * @property {string} cuit
 * @property {string} p
 * @property {string|null|undefined} tr
 * @property {string} periodo
 * @property {number} dias
 * @property {number} horas
 * @property {number} remTotal
 * @property {number} remImpSs
 * @property {number} remRegEsp
 * @property {number} sac
 * @property {boolean} remImpSsEsCero
 */

/**
 * @typedef {Object} ResumenAdjustment
 * @property {string} periodo
 * @property {string} razonSocial
 * @property {string} cuit
 * @property {"REM_IMP_SS_EN_CERO"} motivo
 * @property {false} computable
 */

const normalizeWhitespace = (value) => String(value || "")
    .replace(/\u00A0/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const normalizeHeaderText = (value) => removeDiacritics(normalizeWhitespace(value))
    .toUpperCase()
    .replace(/[.:;()-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const normalizePeriodo = (periodo) => {
    const [mm, yyyy] = String(periodo || "").split("/");
    if (!yyyy) return null;
    const month = Number(mm);
    const year = Number(yyyy);
    if (!Number.isInteger(month) || !Number.isInteger(year)) return null;
    if (month < 1 || month > 12 || year < 1900 || year > 2100) return null;
    return `${String(month).padStart(2, "0")}/${String(year)}`;
};

const parseArNumber = (raw) => {
    const v = normalizeWhitespace(raw)
        .replace(/[^\d,.-]/g, "")
        .trim();
    if (!v) return Number.NaN;
    if (/^-+$/.test(v)) return 0;

    if (v.includes(",")) {
        return Number(v.replace(/\./g, "").replace(",", "."));
    }

    // Si hay mas de un punto, probablemente son miles.
    const points = (v.match(/\./g) || []).length;
    if (points > 1) {
        return Number(v.replace(/\./g, ""));
    }

    return Number(v);
};

const parseNonNegativeInt = (raw) => {
    const n = parseArNumber(raw);
    if (!Number.isFinite(n)) return Number.NaN;
    return Math.round(n);
};

const isHeaderLine = (line) => {
    const normalized = normalizeHeaderText(line);
    if (!normalized) return false;

    const containsCoreHeader = normalized.includes('RAZON SOCIAL') && normalized.includes('PERIODO') && normalized.includes('REM');
    const containsAltHeader = normalized.includes('REM IMP SS') && normalized.includes('REM REG ESP') && normalized.includes('S A C');
    const containsDiasHorasHeader = normalized.includes('DIAS') && normalized.includes('HORAS') && normalized.includes('PERIODO');
    const containsSectionBanner = normalized.includes('APORTES EN RELACION DE DEPENDENCIA') && normalized.includes('07/94');
    const containsTotalsBanner = normalized.includes('TOTAL REM IMP SS') && normalized.includes('REM REG ESP');
    return containsCoreHeader || containsAltHeader || containsDiasHorasHeader || containsSectionBanner || containsTotalsBanner;
};

const LEADING_NOISE_CHUNKS = [
    /^DESDE\s+07\/94\b\s*/i,
    /^APORTES\s+EN\s+RELACI[ÓO]N\s+DE\s+DEPENDENCIA\s+POSTERIORES\s+AL\s+07\/94\s*(?:\(\s*INCLUSIVE\s*\))?\s*/i,
    /^RAZ[ÓO]N?\s+SOCIAL\s+CUIT\s+P\s+T\.?R\.?\s+PERIODO\s+DIAS\s+HORAS\s+REM\.?\s+TOTAL\s+REM\.?\s+IMP\.?\s+SS\s+REM\.?\s+REG\.?\s+ESP\.?\s+S\.?A\.?C\.?\s*/i,
    /^TOTAL\s+REM\.?\s+IMP\.?\s+SS\s+REM\.?\s+REG\.?\s+ESP\.?\s+S\.?A\.?C\.?\s*/i,
    // Variante flexible para saltos de página: usa [\w\s.,]+ (no [^a-z] que con flag /i excluye también mayúsculas)
    /^TOTAL\s+[\w\s.,]+?S\.?A\.?C\.?\s+/i,
];

const EMBEDDED_NOISE_CHUNKS = [
    /\bDESDE\s+07\/94\b/gi,
    /\bAPORTES\s+EN\s+RELACI[ÓO]N\s+DE\s+DEPENDENCIA\s+POSTERIORES\s+AL\s+07\/94\s*(?:\(\s*INCLUSIVE\s*\))?/gi,
    /\bRAZ[ÓO]N?\s+SOCIAL\s+CUIT\s+P\s+T\.?R\.?\s+PERIODO\s+DIAS\s+HORAS\s+REM\.?\s+TOTAL\s+REM\.?\s+IMP\.?\s+SS\s+REM\.?\s+REG\.?\s+ESP\.?\s+S\.?A\.?C\.?\b/gi,
    /\bTOTAL\s+REM\.?\s+IMP\.?\s+SS\s+REM\.?\s+REG\.?\s+ESP\.?\s+S\.?A\.?C\.?\b/gi,
    // Variante flexible: coincide con encabezados de columnas financieras en cualquier posición
    /\bTOTAL\s+[\w\s.,]+?S\.?A\.?C\.(?=\s)/gi,
];

const stripLeadingNoiseChunks = (line) => {
    let current = normalizeWhitespace(line);
    let changed = true;

    while (changed && current) {
        changed = false;
        for (const pattern of LEADING_NOISE_CHUNKS) {
            const next = normalizeWhitespace(current.replace(pattern, ''));
            if (next !== current) {
                current = next;
                changed = true;
            }
        }
    }

    return current;
};

const stripEmbeddedNoiseChunks = (line) => {
    let current = normalizeWhitespace(line);
    let changed = true;

    while (changed && current) {
        changed = false;
        for (const pattern of EMBEDDED_NOISE_CHUNKS) {
            const next = normalizeWhitespace(current.replace(pattern, ' '));
            if (next !== current) {
                current = next;
                changed = true;
            }
        }
    }

    return normalizeWhitespace(
        current
            .replace(/(?:^|\s)[.:;]+(?=\s|$)/g, ' ')
    );
};

const sanitizeDesde0794Line = (line) => {
    const sinEncabezadosInicio = stripLeadingNoiseChunks(line);
    return stripEmbeddedNoiseChunks(sinEncabezadosInicio);
};

const normalizeCuitToken = (raw) => {
    const digits = String(raw || "").replace(/\D/g, "");
    if (digits.length !== 11) return null;
    return `${digits.slice(0, 2)}-${digits.slice(2, 10)}-${digits.slice(10)}`;
};

const readCuitFromTail = (tokens, cuitLastIndex) => {
    if (cuitLastIndex < 0) return null;

    const oneToken = normalizeCuitToken(tokens[cuitLastIndex]);
    if (oneToken) {
        return { cuit: oneToken, cuitStart: cuitLastIndex, cuitEnd: cuitLastIndex };
    }

    if (cuitLastIndex >= 2) {
        const partA = tokens[cuitLastIndex - 2];
        const partB = tokens[cuitLastIndex - 1];
        const partC = tokens[cuitLastIndex];
        if (/^\d{2}$/.test(partA) && /^\d{8}$/.test(partB) && /^\d$/.test(partC)) {
            const cuit = normalizeCuitToken(`${partA}${partB}${partC}`);
            if (cuit) {
                return { cuit, cuitStart: cuitLastIndex - 2, cuitEnd: cuitLastIndex };
            }
        }
    }

    return null;
};

/**
 * Devuelve el texto entre DESDE 07/94 y HISTORICO DE AUTONOMOS.
 * @param {string} text
 * @returns {string}
 */
export const extractDesde0794Block = (text) => {
    const source = String(text || "");
    const startMatch = TITULO_INICIO_REGEX.exec(source);
    if (!startMatch) return "";

    const fromStart = source.slice(startMatch.index);
    const endMatch = TITULO_FIN_REGEX.exec(fromStart);
    if (!endMatch) return fromStart;

    return fromStart.slice(0, endMatch.index);
};

/**
 * Limpia lineas de ruido del bloque DESDE 07/94.
 * Incluye recuperación de razón social concatenada con encabezados en saltos de página.
 * @param {string} block
 * @returns {string[]}
 */
export const cleanDesde0794Lines = (block) => {
    return String(block || "")
        .split(/\r?\n/)
        // Pre-rescue (antes de sanitize): detecta "TOTAL ... S.A.C. <empresa>" de saltos de página.
        // Usa matchAll con flag global para encontrar la ÚLTIMA ocurrencia de S.A.C. en la línea
        // (más robusta que un único match, independiente de la variante exacta de formato del PDF).
        .map((rawLine) => {
            const norm = normalizeWhitespace(rawLine);
            if (!/^TOTAL[\s,.]/i.test(norm)) return rawLine;
            const sacMatches = [...norm.matchAll(/S\.?A\.?C\.?\s+/gi)];
            if (sacMatches.length === 0) return '';  // TOTAL puro sin S.A.C. → descartar
            const lastMatch = sacMatches[sacMatches.length - 1];
            const afterSac = norm.slice(lastMatch.index + lastMatch[0].length).trim();
            return afterSac.length >= 2 ? afterSac : '';  // empresa encontrada o descartar
        })
        .map((line) => sanitizeDesde0794Line(line))
        .map((line) => normalizeWhitespace(line))
        .filter((line) => line.length > 0)
        .filter((line) => !/^\d+\s*\/\s*\d+$/.test(line))
        .filter((line) => !/^APELLIDO\s+Y\s+NOMBRE/i.test(line))
        .filter((line) => !/^CUIT\/?L[:\s]/i.test(line))
        .filter((line) => !/^CUIL[:\s]/i.test(line))
        .filter((line) => !/^DESDE\s+07\/94/i.test(line))
        .filter((line) => !/^HIST(?:[ÓO]RICO|ORICO)\s+DE\s+AUT[ÓO]NOM/i.test(line))
        .filter((line) => !/^P[ÁA]GINA[:\s]/i.test(line))
        // Recuperación de saltos de página: si la línea aún parece un encabezado (stripLeadingNoiseChunks
        // no lo limpió por variante de formato) pero tiene razón social concatenada después de "S.A.C.",
        // extrae solo la parte de datos.
        .map((line) => {
            if (!isHeaderLine(line)) return line;
            // Rescata razón social concatenada al encabezado en saltos de página.
            // Sin flag /i: [A-ZÁÉÍÓÚÜÑ] captura solo mayúsculas para asegurar que es inicio de nombre de empresa.
            const match = line.match(/S\.?A\.?C\.?\s+([A-ZÁÉÍÓÚÜÑ].+)/);
            if (match) return normalizeWhitespace(match[1]);
            return '';
        })
        .filter((line) => line.length > 0)
        .filter((line) => !isHeaderLine(line));
};

const isRowTailComplete = (text) => {
    const tokens = normalizeWhitespace(text).split(" ");
    if (tokens.length < 9) return false;

    const sac = parseArNumber(tokens[tokens.length - 1]);
    const remRegEsp = parseArNumber(tokens[tokens.length - 2]);
    const remImpSs = parseArNumber(tokens[tokens.length - 3]);
    const remTotal = parseArNumber(tokens[tokens.length - 4]);
    const horas = parseArNumber(tokens[tokens.length - 5]);
    const dias = parseArNumber(tokens[tokens.length - 6]);
    const periodo = tokens[tokens.length - 7];

    if (!PERIODO_REGEX.test(periodo)) return false;
    if (![dias, horas, remTotal, remImpSs, remRegEsp, sac].every((n) => Number.isFinite(n))) {
        return false;
    }

    return true;
};

/**
 * Indica si el texto acumulado ya representa una fila completa.
 * @param {string} text
 * @returns {boolean}
 */
export const isDesde0794CompleteRow = (text) => {
    const normalized = normalizeWhitespace(text);
    if (!normalized) return false;
    return parseDesde0794Row(normalized) !== null || isRowTailComplete(normalized);
};

/**
 * Reconstruye filas logicas concatenando lineas hasta cerrar una fila completa.
 * @param {string[]} lines
 * @returns {string[]}
 */
export const rebuildDesde0794Rows = (lines) => {
    const rows = [];
    let buffer = "";

    for (const line of lines || []) {
        let clean = sanitizeDesde0794Line(line);
        if (!clean) continue;
        if (isHeaderLine(clean)) continue;

        // Segunda capa de rescue: si la línea limpia aún empieza con TOTAL, extraer empresa
        if (/^TOTAL[\s,.]/i.test(clean)) {
            const sacMatches = [...clean.matchAll(/S\.?A\.?C\.?\s+/gi)];
            if (sacMatches.length > 0) {
                const lastMatch = sacMatches[sacMatches.length - 1];
                const afterSac = clean.slice(lastMatch.index + lastMatch[0].length).trim();
                clean = afterSac.length >= 2 ? afterSac : '';
            } else {
                clean = '';
            }
        }
        if (!clean) continue;

        buffer = buffer ? `${buffer} ${clean}` : clean;

        if (isDesde0794CompleteRow(buffer)) {
            rows.push(buffer);
            buffer = "";
        }
    }

    if (buffer && isDesde0794CompleteRow(buffer)) {
        rows.push(buffer);
    }

    return rows;
};

/**
 * Parsea una fila reconstruida de DESDE 07/94.
 * @param {string} row
 * @returns {Desde0794Row|null}
 */
export const parseDesde0794Row = (row) => {
    const normalized = normalizeWhitespace(row);
    if (!normalized) return null;

    const tokens = normalized.split(" ");
    if (tokens.length < 10) return null;

    const sacRaw = tokens.pop();
    const remRegEspRaw = tokens.pop();
    const remImpSsRaw = tokens.pop();
    const remTotalRaw = tokens.pop();
    const horasRaw = tokens.pop();
    const diasRaw = tokens.pop();
    const periodoRaw = tokens.pop();

    if (!PERIODO_REGEX.test(periodoRaw)) return null;

    const sac = parseArNumber(sacRaw);
    const remRegEsp = parseArNumber(remRegEspRaw);
    const remImpSs = parseArNumber(remImpSsRaw);
    const remTotal = parseArNumber(remTotalRaw);
    const horas = parseNonNegativeInt(horasRaw);
    const dias = parseNonNegativeInt(diasRaw);

    if (![sac, remRegEsp, remImpSs, remTotal, horas, dias].every((n) => Number.isFinite(n))) {
        return null;
    }

    // Parte izquierda: razon social + cuit + P (+ TR opcional)
    if (tokens.length < 3) return null;

    let tr = null;
    let p = null;
    let cuit = null;
    let razonSocial = null;

    // Variante con TR: ... <CUIT> <P> <TR>
    if (tokens.length >= 4) {
        const trCandidate = tokens[tokens.length - 1];
        const pCandidate = tokens[tokens.length - 2];
        const cuitInfo = readCuitFromTail(tokens, tokens.length - 3);

        if (cuitInfo && /^[A-Z0-9]{1,4}$/i.test(pCandidate) && /^[A-Z0-9]{1,4}$/i.test(trCandidate)) {
            const razonCandidate = normalizeWhitespace(tokens.slice(0, cuitInfo.cuitStart).join(" "));
            if (razonCandidate) {
                tr = trCandidate;
                p = pCandidate;
                cuit = cuitInfo.cuit;
                razonSocial = razonCandidate;
            }
        }
    }

    // Variante sin TR: ... <CUIT> <P>
    if (!razonSocial) {
        const pCandidate = tokens[tokens.length - 1];
        const cuitInfo = readCuitFromTail(tokens, tokens.length - 2);
        if (cuitInfo && /^[A-Z0-9]{1,4}$/i.test(pCandidate)) {
            const razonCandidate = normalizeWhitespace(tokens.slice(0, cuitInfo.cuitStart).join(" "));
            if (razonCandidate) {
                tr = null;
                p = pCandidate;
                cuit = cuitInfo.cuit;
                razonSocial = razonCandidate;
            }
        }
    }

    if (!razonSocial || !p || !cuit || !CUIT_REGEX.test(cuit)) {
        return null;
    }

    if (!razonSocial) return null;

    const periodo = normalizePeriodo(periodoRaw);
    if (!periodo) return null;

    return {
        razonSocial,
        cuit,
        p,
        tr,
        periodo,
        dias,
        horas,
        remTotal,
        remImpSs,
        remRegEsp,
        sac,
        // REM IMP. SS en [0,10]: valores muy bajos indican período no aportado (E7)
        remImpSsEsCero: remImpSs >= 0 && remImpSs <= 10,
    };
};

/**
 * Orquesta parseo de la seccion DESDE 07/94.
 * @param {string} text
 * @returns {Desde0794Row[]}
 */
export const parseDesde0794Section = (text) => {
    const block = extractDesde0794Block(text);
    if (!block) return [];

    const lines = cleanDesde0794Lines(block);
    const logicalRows = rebuildDesde0794Rows(lines);

    return logicalRows
        // E10: re-sanitizar la fila REENSAMBLADA. Cuando el encabezado de columnas se
        // parte por salto de página en líneas sueltas ("TOTAL" / "REM" / "IMP. SS" /
        // "REM." / "REG. ESP." / "S.A.C."), cada línea individual no matchea isHeaderLine
        // (que exige la secuencia completa) y se acumulan en el buffer, contaminando la
        // razón social del primer registro de cada página. Al sanitizar el string ya
        // reensamblado, el patrón embebido "TOTAL ... S.A.C." lo limpia correctamente.
        .map((row) => parseDesde0794Row(sanitizeDesde0794Line(row)))
        .filter((row) => row !== null);
};

/**
 * Genera ajustes para el resumen a partir de DESDE 07/94.
 * Regla: un período se marca NO computable solo si REM IMP. SS está en [0,10]
 * en TODAS las filas de ese período.
 *
 * E11: si un mismo mes tiene varias filas (varios empleadores) y al menos una
 * tiene aporte real (REM IMP. SS > 10), el mes SÍ es computable y no debe generar
 * ajuste. Filtrar fila por fila (como antes) descontaba el mes aunque un empleador
 * hubiera aportado, provocando una doble resta cuando además estaba superpuesto.
 *
 * @param {Desde0794Row[]} rows
 * @returns {ResumenAdjustment[]}
 */
export const getResumenAdjustmentsFromDesde0794 = (rows) => {
    // Agrupar por período preservando el orden de aparición.
    const porPeriodo = new Map(); // periodo -> { hayComputable, primeraNoComputable }
    for (const row of rows || []) {
        if (!row || !row.periodo) continue;
        const esNoComputable = row.remImpSs >= 0 && row.remImpSs <= 10;
        let entry = porPeriodo.get(row.periodo);
        if (!entry) {
            entry = { hayComputable: false, primeraNoComputable: null };
            porPeriodo.set(row.periodo, entry);
        }
        if (esNoComputable) {
            if (!entry.primeraNoComputable) entry.primeraNoComputable = row;
        } else {
            entry.hayComputable = true;
        }
    }

    const adjustments = [];
    for (const [periodo, entry] of porPeriodo.entries()) {
        // Solo no computable si NINGUNA fila del período tiene aporte real (> 10).
        if (!entry.hayComputable && entry.primeraNoComputable) {
            const row = entry.primeraNoComputable;
            adjustments.push({
                periodo,
                razonSocial: row.razonSocial,
                cuit: row.cuit,
                motivo: "REM_IMP_SS_EN_CERO",
                computable: false,
            });
        }
    }
    return adjustments;
};


